import { faCheck, faInfoCircle, faWarning, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material';
import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export type ProcessingStatusProps = {
  status: 'COMPLETED' | 'FAILED' | 'IN_PROGRESS';
  fileName: string;
  runID: string;
  textOnComplete?: string;
};

export function ProcessingStatus({ status, fileName, runID, textOnComplete }: Readonly<ProcessingStatusProps>) {
  const { t } = useTranslation('common');

  if (status === 'COMPLETED') {
    return (
      <Status
        text={t('fileUpload.processingStatus.completedTitle', { fileName })}
        icon={faCheck}
        textOnComplete={textOnComplete}
      />
    );
  }

  if (status === 'FAILED') {
    return (
      <Status
        text={t('fileUpload.processingStatus.failedTitle', { fileName })}
        icon={faWarning}
        footer={
          <>
            <p>{t('fileUpload.processingStatus.errorReference', { context: 'withMessage', message: runID })}</p>
            <p>{t('fileUpload.processingStatus.tryAgain')}</p>
          </>
        }
      />
    );
  }

  if (status === 'IN_PROGRESS') {
    return (
      <Status
        text={t('fileUpload.processingStatus.processing', { fileName })}
        icon={faInfoCircle}
        footer={<p>{t('fileUpload.processingStatus.dontLeave')}</p>}
      />
    );
  }

  /* istanbul ignore next - should never get here */
  return null;
}

interface StatusProps {
  text: string;
  icon: IconDefinition;
  footer?: ReactNode;
  textOnComplete?: string;
}

function Status({ text, icon, footer, textOnComplete }: Readonly<StatusProps>) {
  return (
    <StatusMessage role="alert">
      <StatusTitle data-testid="status-title">
        <FontAwesomeIcon data-testid="status-icon" icon={icon} size="2x" />
        <Trans>{text}</Trans>
      </StatusTitle>
      {footer}
      {textOnComplete && <p>{textOnComplete}</p>}
    </StatusMessage>
  );
}

const StatusMessage = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const StatusTitle = styled('p')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 1rem;
`;
